import { WORKSPACE_PATH, paths } from '@/paths';
import { useCreateChatService } from '@/services/chat';
import { useChat } from '@ai-sdk/react';
import { DtUIMessage } from '@ditto/shared';

import { marked } from 'marked';
import { useCallback, useMemo } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';

export type UseChatParams = Parameters<typeof useChat<DtUIMessage>>[0];
type UseChatReturn = ReturnType<typeof useChat<DtUIMessage>>;

type HandleSubmitType = UseChatReturn['sendMessage'];
type HandleRegenerateType = UseChatReturn['regenerate'];

export type HandleSubmitMessageType = Parameters<HandleSubmitType>[0];
export type HandleSubmitOptionsType = Parameters<HandleSubmitType>[1];

export type HandleRegenerateOptionsType = Parameters<HandleRegenerateType>[0];

export function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  return tokens.map(token => token.raw);
}

type ChatLocationState = {
  initialMessage: HandleSubmitMessageType;
};

export function useChatLocation(): ChatLocationState {
  const location = useLocation();

  const initialMessage = useMemo(() => {
    const locationInitialMessage = location.state?.initialMessage;

    const initialMessage =
      locationInitialMessage &&
      typeof locationInitialMessage === 'object' &&
      'text' in locationInitialMessage
        ? locationInitialMessage
        : undefined;

    return initialMessage;
  }, [location]);

  return {
    initialMessage,
  };
}

export function useChatNavigate(options?: { projectId?: string }) {
  const navigate = useNavigate();
  const { workspaceUname } = useParams();
  const { createChat, isCreating } = useCreateChatService();

  if (!workspaceUname) {
    throw new Error('Workspace uname is required');
  }

  const navigateToChat = useCallback(
    async (chatLocationState: ChatLocationState) => {
      // Create the chat first and get the ID from the response
      const result = await createChat({
        region: chatLocationState.initialMessage?.metadata?.region ?? 'WORLD',
        projectId: options?.projectId,
      });

      // Then navigate to the chat page with the created chat ID
      const chatsPath = generatePath(`${WORKSPACE_PATH}/${paths.chats}`, {
        workspaceUname,
      });
      navigate(`${chatsPath}/${result.id}`, {
        state: {
          ...chatLocationState,
        },
      });
    },
    [workspaceUname, navigate, createChat, options?.projectId]
  );

  return { navigateToChat, isCreatingChat: isCreating };
}
